import { useState, useEffect, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { authHeaders } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import type { BatchOrigin, ApiErrorBody } from '../../types';

interface Props {
  onClose: () => void;
  onOriginsChange: (origins: BatchOrigin[]) => void;
}

export default function BatchOriginsModal({ onClose, onOriginsChange }: Props) {
  const [origins, setOrigins] = useState<BatchOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    apiFetch('/api/batch-origins', { headers: authHeaders() })
      .then(r => r.json() as Promise<BatchOrigin[]>)
      .then(data => setOrigins(data ?? []))
      .catch(() => setLoadError('Failed to load origins'))
      .finally(() => setLoading(false));
  }, []);

  const pushUpdate = (updated: BatchOrigin[]) => {
    setOrigins(updated);
    onOriginsChange(updated);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const res = await apiFetch('/api/batch-origins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: newName.trim() }),
      });
      const data: ApiErrorBody & { data?: BatchOrigin } = await res.json();
      if (!res.ok) { setCreateError(data.message ?? 'Failed to create origin'); return; }
      const updated = [...origins, data.data!].sort((a, b) => a.name.localeCompare(b.name));
      pushUpdate(updated);
      setNewName('');
    } catch {
      setCreateError('Failed to create origin');
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (e: FormEvent, id: number) => {
    e.preventDefault();
    setRenameSaving(true);
    setRenameError(null);
    try {
      const res = await apiFetch(`/api/batch-origins/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: renameValue.trim() }),
      });
      const data: ApiErrorBody & { data?: BatchOrigin } = await res.json();
      if (!res.ok) { setRenameError(data.message ?? 'Failed to rename'); return; }
      const updated = origins
        .map(o => o.id === id ? { ...o, name: data.data!.name } : o)
        .sort((a, b) => a.name.localeCompare(b.name));
      pushUpdate(updated);
      setRenamingId(null);
    } catch {
      setRenameError('Failed to rename origin');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleToggleActive = async (origin: BatchOrigin) => {
    setTogglingId(origin.id);
    try {
      const res = await apiFetch(`/api/batch-origins/${origin.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ isActive: !origin.isActive }),
      });
      if (!res.ok) return;
      const updated = origins.map(o => o.id === origin.id ? { ...o, isActive: !o.isActive } : o);
      pushUpdate(updated);
    } catch {
      // silently fail
    } finally {
      setTogglingId(null);
    }
  };

  const modal = (
    <div className='batch-origins-backdrop' onClick={onClose}>
      <div className='batch-origins-modal' onClick={e => e.stopPropagation()}>
        <div className='batch-origins-header'>
          <h3 className='batch-origins-title'>Manage Batch Origins</h3>
          <button className='batch-origins-close' onClick={onClose}>✕</button>
        </div>

        <form className='origins-add-form' onSubmit={handleCreate}>
          <div className='user-form-field'>
            <input
              placeholder='New origin name (e.g. Company, MB2, School)'
              value={newName}
              onChange={e => setNewName(e.target.value)}
              required
            />
          </div>
          <button type='submit' className='user-submit-btn' disabled={creating}>
            {creating ? 'Adding…' : 'Add Origin'}
          </button>
        </form>
        {createError && <p className='user-form-error' style={{ margin: '0 0 12px' }}>{createError}</p>}

        {loading && <p className='user-loading'>Loading…</p>}
        {loadError && <p className='user-error'>{loadError}</p>}

        {!loading && origins.length === 0 && (
          <p className='user-empty'>No origins yet.</p>
        )}

        {!loading && origins.length > 0 && (
          <div className='table-responsive'>
            <table className='user-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {origins.map(o => (
                  <tr key={o.id}>
                    <td>
                      {renamingId === o.id ? (
                        <form className='group-rename-form' onSubmit={e => handleRename(e, o.id)}>
                          <input
                            className='group-rename-input'
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            autoFocus
                            required
                          />
                          <button type='submit' className='group-rename-save-btn' disabled={renameSaving}>
                            {renameSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type='button'
                            className='user-cancel-btn'
                            onClick={() => { setRenamingId(null); setRenameError(null); }}
                          >
                            Cancel
                          </button>
                          {renameError && <span className='group-rename-error'>{renameError}</span>}
                        </form>
                      ) : (
                        o.name
                      )}
                    </td>
                    <td>
                      <button
                        className={`group-tagging-badge${o.isActive ? ' group-tagging-badge--on' : ' group-tagging-badge--off'}`}
                        onClick={() => handleToggleActive(o)}
                        disabled={togglingId === o.id}
                        aria-label={`${o.isActive ? 'Deactivate' : 'Activate'} ${o.name}`}
                      >
                        {o.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className='user-edit-btn'
                        onClick={() => { setRenamingId(o.id); setRenameValue(o.name); setRenameError(null); }}
                      >
                        Rename
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
